// src/context/AuthContext.jsx

import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, getDoc, collection, query, getDocs, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { requestNotificationPermissionAndSaveToken } from '../utils/notificationUtils';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Utility function to initialize raw materials
const initializeRawMaterials = async (companyId) => {
  try {
    // Check if raw materials already exist for this company
    const rawMaterialsRef = collection(db, 'companies', companyId, 'rawMaterials');
    const q = query(rawMaterialsRef);
    const querySnapshot = await getDocs(q);

    // If raw materials already exist, don't initialize
    if (!querySnapshot.empty) {
      console.log('Raw materials already exist for this company');
      return;
    }

    // Sample raw materials data
    const sampleRawMaterials = [
      {
        name: 'Çelik Levha',
        unit: 'kg',
        quantity: 1000,
        lastPurchasePrice: 15.50,
        supplier: 'Demir Çelik Sanayi',
        description: '3mm kalınlıkta çelik levha',
        category: 'Metal'
      },
      {
        name: 'Alüminyum Profil',
        unit: 'kg',
        quantity: 500,
        lastPurchasePrice: 25.75,
        supplier: 'Alüminyum Sanayi',
        description: '20x20 mm kare profil',
        category: 'Metal'
      },
      {
        name: 'PVC Granül',
        unit: 'kg',
        quantity: 800,
        lastPurchasePrice: 12.30,
        supplier: 'Plastik Malzeme',
        description: 'Beyaz renk PVC granül',
        category: 'Plastik'
      },
      {
        name: 'Boyalar',
        unit: 'lt',
        quantity: 200,
        lastPurchasePrice: 45.00,
        supplier: 'Kimya Sanayi',
        description: 'Su bazlı boyalar',
        category: 'Kimyasal'
      },
      {
        name: 'Vida ve Somun',
        unit: 'adet',
        quantity: 10000,
        lastPurchasePrice: 0.10,
        supplier: 'Vida Sanayi',
        description: 'Çeşitli ebatlarda vida ve somun',
        category: 'Donanım'
      }
    ];

    // Add sample raw materials to the database
    for (const material of sampleRawMaterials) {
      await addDoc(rawMaterialsRef, material);
    }

    console.log('Raw materials initialized successfully');
  } catch (error) {
    console.error('Error initializing raw materials:', error);
  }
};

// Utility function to initialize raw material orders
const initializeRawMaterialOrders = async (companyId) => {
  try {
    // Check if raw material orders already exist for this company
    const rawMaterialOrdersRef = collection(db, 'companies', companyId, 'rawMaterialOrders');
    const q = query(rawMaterialOrdersRef);
    const querySnapshot = await getDocs(q);

    // If raw material orders already exist, don't initialize
    if (!querySnapshot.empty) {
      console.log('Raw material orders already exist for this company');
      return;
    }

    console.log('Raw material orders initialized successfully');
  } catch (error) {
    console.error('Error initializing raw material orders:', error);
  }
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const hasInitializedRef = React.useRef(false);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setUserData(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', currentUser.uid);

    // Main user doc listener
    const unsubscribeUser = onSnapshot(userDocRef, (userDocSnap) => {
      if (userDocSnap.exists()) {
        const baseUserData = userDocSnap.data();

        if (baseUserData.role === 'admin') {
          setUserData({
            ...baseUserData,
            companyId: currentUser.uid,
            permissions: {
              dashboard: true, raporlar: true, kasa: true, musteriler: true,
              tedarikciler: true, stok: true, alisFaturalari: true, faturalar: true,
              siparisler: true, istekSiparisler: true, ayarlar: true, stokGirisi: true,
              viewPrices: true,
            }
          });
          setLoading(false);

          if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            if (baseUserData.pushNotifications !== false) {
              requestNotificationPermissionAndSaveToken(currentUser.uid).catch(console.error);
            }
            initializeRawMaterials(currentUser.uid);
            initializeRawMaterialOrders(currentUser.uid);
          }

        } else if (baseUserData.role === 'personel') {
          // For personnel, we need to also listen to their specific permissions
          const personnelDocRef = doc(db, 'companies', baseUserData.companyId, 'personnel', currentUser.uid);

          getDoc(personnelDocRef).then((personnelDocSnap) => {
            const permissions = personnelDocSnap.exists() ? (personnelDocSnap.data().permissions || {}) : {};
            setUserData({ ...baseUserData, permissions });
            setLoading(false);

            if (!hasInitializedRef.current) {
              hasInitializedRef.current = true;
              if (baseUserData.pushNotifications !== false) {
                requestNotificationPermissionAndSaveToken(currentUser.uid).catch(console.error);
              }
              initializeRawMaterials(baseUserData.companyId);
              initializeRawMaterialOrders(baseUserData.companyId);
            }
          }).catch(err => {
            console.error("Error fetching personnel permissions:", err);
            setUserData({ ...baseUserData, permissions: {} });
            setLoading(false);
          });
        } else if (baseUserData.role === 'customer') {
          setUserData({
            ...baseUserData,
            permissions: { portal: true, viewPrices: true }
          });
          setLoading(false);
        } else if (baseUserData.role === 'store') {
          setUserData({
            ...baseUserData,
            permissions: { 
              magazaPaneli: true, 
              stok: true,           // Kendi stoğunu yönetme ve (eğer izinliyse) ana stoğu görme
              perakendeSatis: true 
            }
          });
          setLoading(false);
        }
      } else {
        setUserData(null);
        setLoading(false);
      }
    }, (error) => {
      console.error("Auth user listener error:", error);
      setLoading(false);
    });

    return () => unsubscribeUser();
  }, [currentUser, refreshTrigger]);

  const refreshPermissions = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const value = {
    currentUser,
    userData,
    loading,
    canViewPrices: userData?.role === 'admin' || userData?.permissions?.viewPrices === true,
    refreshPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthContext;